/* eslint-disable react/jsx-props-no-spreading */
import { Form, useForm } from 'react-hook-form';
import {
  MemberDrawer,
  MemberDrawerFooter,
  MemberDrawerHeader
} from '@bo-components/members/drawer';
import { PrimaryButton, ResetIconButton } from '@library/components/buttons';
import { useCallback, useEffect } from 'react';
import { MEMBER_ADDITIONAL_FILTERS } from '@bo-constants/member/membersFilters';
import { MemberFiltersFormGroup } from '@bo-components/members/membersList';
import type { SearchFilters } from '@library/types/members';
import { useDrawerAnimation } from '@bo-hooks/useDrawerAnimation';

export interface MemberFiltersDrawerProps {
  memberSearchFilters: SearchFilters;
  onClose: () => void;
  onResetFilters: () => void;
  onSubmit: (filters: SearchFilters) => void;
}

export const MemberFiltersDrawer = ({
  memberSearchFilters,
  onClose,
  onResetFilters,
  onSubmit
}: MemberFiltersDrawerProps) => {
  const { isOpen, onCloseDrawer } = useDrawerAnimation({
    onClose
  });

  const methods = useForm<SearchFilters>({
    defaultValues: memberSearchFilters
  });
  const { control, handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    reset(memberSearchFilters);
  }, [memberSearchFilters, reset]);

  const FormGroups = useCallback(
    () =>
      MEMBER_ADDITIONAL_FILTERS.map((filterGroup) => (
        <MemberFiltersFormGroup
          key={filterGroup.groupName}
          control={control}
          filterGroup={filterGroup}
          setValue={setValue}
        />
      )),
    [control, setValue]
  );

  return (
    <MemberDrawer isOpen={isOpen} onCloseDrawer={onCloseDrawer}>
      {/* Header */}
      <MemberDrawerHeader onClose={onCloseDrawer}>
        <p className="text-soClean-180 text-xs leading-none">All Members</p>
        <p className="text-soClean-100 leading-none">Additional Filters</p>
      </MemberDrawerHeader>

      {/* Content */}
      <Form
        className="flex h-[calc(100%-7rem)] w-full flex-wrap gap-4 overflow-y-auto p-6"
        control={control}
      >
        <FormGroups />
      </Form>

      {/* Footer */}
      <MemberDrawerFooter>
        <ResetIconButton
          className="disabled:text-soClean-140 m-0 shrink-0 basis-28 p-0"
          disabled={false}
          label="Reset Filters"
          onClick={onResetFilters}
        />
        <PrimaryButton
          className="ml-4 px-4 py-2 text-sm font-medium"
          title="Apply"
          handleOnClick={handleSubmit(onSubmit)}
        >
          Apply
        </PrimaryButton>
      </MemberDrawerFooter>
    </MemberDrawer>
  );
};
